import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import { showToast } from '~/utils/helpers';
import biomeApi from './biome.api';
import { handleThunkException } from '~/services/thunk.service';
import { clearApiValidationErrors } from '~/stores/redux/common/apiValidationErrorSlice';

export const fetchBiomeApi = createAsyncThunk(
    'cardSaga/Biome/fetchBiomeApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await biomeApi.getAll();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const deleteBiomeItemApi = createAsyncThunk(
    'cardSaga/Biome/deleteBiomeItemApi',
    async ({ id, navigate }, { dispatch }) => {
        try {
            await biomeApi.delete(id);
            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const createNewBiomeApi = createAsyncThunk(
    'cardSaga/Biome/createNewBiomeApi',
    async ({ navigate, values, formikBag }, { dispatch }) => {
        try {
            dispatch(requestStarted());
            await biomeApi.store(values);
            dispatch(requestFinished());

            showToast('Biome saved successfully!');
            formikBag.setFieldValue('biome_name', '', false);
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const updateBiomeApi = createAsyncThunk(
    'cardSaga/Biome/updateBiomeApi',
    async ({ navigate, id, values, setDisabledBiome }, { dispatch }) => {
        try {
            dispatch(requestStarted());
            await biomeApi.update(id, values);
            dispatch(requestFinished());

            showToast('Biome saved successfully!');
            setDisabledBiome(true);

            return {
                id: id,
                biome_name: values.biome_name,
            };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const fetchBiomeAdventureApi = createAsyncThunk(
    'cardSaga/Biome/fetchBiomeAdventureApi',
    async ({ navigate, id }, { dispatch }) => {
        try {
            dispatch(requestStarted());
            const data = await biomeApi.getBiomeAdventures(id);
            dispatch(requestFinished());

            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const updateAdventureQtyApi = createAsyncThunk(
    'cardSaga/Biome/updateAdventureQtyApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { biomeId, navigate } = params;
        const state = getState();
        const editedAdventureRow = state.cardSaga.biome.editedAdventureRow;

        try {
            dispatch(requestStarted());

            await biomeApi.updateAdventureQty(biomeId, editedAdventureRow.id, editedAdventureRow.qty);

            showToast('Quantity updated successfully!');

            dispatch(clearApiValidationErrors());
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const biomeSlice = createSlice({
    name: 'cardSaga/Biome',
    initialState: {
        biomeRows: [],
        adventureRows: [],
        editedAdventureRow: {},
    },
    reducers: {
        addBiomeAdventureItem(state, { payload }) {
            return {
                ...state,
                adventureRows: [payload, ...state.adventureRows],
            };
        },

        removeBiomeAdventureItem(state, { payload }) {
            return {
                ...state,
                adventureRows: state.adventureRows.filter(({ id }) => id.toString() !== payload),
            };
        },

        editAdventureQty(state, { payload }) {
            state.editedAdventureRow = payload;
        },

        cancelEditAdventureQty(state) {
            state.editedAdventureRow = {};
        },

        updateAdventureQty(state, { payload }) {
            state.editedAdventureRow = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBiomeApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    biomeRows: payload,
                };
            })
            .addCase(fetchBiomeApi.rejected, (state, { payload = [] }) => {
                state.biomeRows = payload;
            });

        builder.addCase(deleteBiomeItemApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                biomeRows: state.biomeRows.filter(({ id }) => {
                    return id !== payload;
                }),
            };
        });

        builder.addCase(createNewBiomeApi.fulfilled, (state) => {
            return {
                ...state,
                biomeRows: [], //clear all rows so that api fetchAll will be called again in index page.
            };
        });

        builder.addCase(updateBiomeApi.fulfilled, (state, { payload }) => {
            if (state.biomeRows.length === 0) return;

            state.biomeRows.map((row) => {
                if (row.id.toString() == payload.id) {
                    row.biome_name = payload.biome_name;
                }
                return row;
            });
        });

        builder.addCase(fetchBiomeAdventureApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                adventureRows: payload,
            };
        });

        builder
            .addCase(updateAdventureQtyApi.fulfilled, (state) => {
                state.adventureRows.forEach((item, i, arr) => {
                    if (item.id === state.editedAdventureRow.id) {
                        arr[i] = state.editedAdventureRow;
                    }
                });
                state.editedAdventureRow = {};
            })
            .addCase(updateAdventureQtyApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });
    },
});

export const {
    addBiomeAdventureItem,
    removeBiomeAdventureItem,
    editAdventureQty,
    cancelEditAdventureQty,
    updateAdventureQty,
} = biomeSlice.actions;
